import React, { useState } from "react";
import { ArrowRight, Check, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { captureAttribution } from "../../lib/attribution";
import { BUSINESS_TYPES, AUDIT_OPTIONS } from "../../data/site";

const inp = "w-full bg-white border border-slate-300 rounded-md px-4 py-3 text-sm text-slate-900 focus:border-alto-red outline-none";
const lbl = "block text-xs font-semibold text-slate-600 mb-1.5";

export default function AuditForm({ campaign = "Lead System Audit" }) {
  const [f, setF] = useState({
    first_name: "", last_name: "", business_name: "", business_type: "", website: "",
    email: "", phone: "", lead_sources: [], leads_per_month: "", tracking_method: "",
    follow_up_speed: "", customer_value: "", biggest_challenge: "", preferred_contact: "Phone", consent: false,
  });
  const [hp, setHp] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => { const v = e.target.value; setF((p) => ({ ...p, [k]: v })); };
  const toggleSource = (s) => setF((p) => ({ ...p, lead_sources: p.lead_sources.includes(s) ? p.lead_sources.filter((x) => x !== s) : [...p.lead_sources, s] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name || (!f.email && !f.phone)) { toast.error("Please add your name and an email or phone."); return; }
    if (!f.consent) { toast.error("Please agree to be contacted."); return; }
    setBusy(true);
    try {
      const attr = captureAttribution();
      await api.post("/public/lead-audit", { data: f, campaign, _hp: hp, ...attr });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="bg-white rounded-2xl p-8 text-center" data-testid="audit-success">
        <div className="w-16 h-16 rounded-full bg-alto-red/10 flex items-center justify-center mx-auto mb-5"><Check className="text-alto-red" size={32} /></div>
        <h3 className="font-display text-2xl font-bold text-slate-900">Your audit request is in.</h3>
        <p className="text-slate-600 mt-3 text-sm max-w-md mx-auto">We'll review how your business captures and follows up with leads, and reach out within one business day with your findings.</p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <a href="tel:+19125550100" className="btn-red font-bold px-6 py-3 rounded-md inline-flex items-center justify-center gap-2 text-sm"><Phone size={16} /> Prefer to talk now?</a>
          <a href="mailto:hello@alltomaidit.com" className="font-bold px-6 py-3 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center justify-center gap-2 text-sm"><Mail size={16} /> Email us</a>
        </div>
        <p className="text-xs text-slate-400 mt-5">Want to go faster? Mention your availability and we'll book a discovery call.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl p-6 sm:p-8 space-y-6" data-testid="audit-form">
      <input type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" aria-hidden />

      <Section title="Your business">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={lbl}>First name *</label><input className={inp} value={f.first_name} onChange={set("first_name")} data-testid="audit-first-name" required /></div>
          <div><label className={lbl}>Last name</label><input className={inp} value={f.last_name} onChange={set("last_name")} data-testid="audit-last-name" /></div>
          <div><label className={lbl}>Business name</label><input className={inp} value={f.business_name} onChange={set("business_name")} data-testid="audit-business-name" /></div>
          <div><label className={lbl}>Business type</label>
            <select className={inp} value={f.business_type} onChange={set("business_type")} data-testid="audit-business-type"><option value="">Select…</option>{BUSINESS_TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div className="sm:col-span-2"><label className={lbl}>Website</label><input className={inp} value={f.website} onChange={set("website")} placeholder="https://" data-testid="audit-website" /></div>
        </div>
      </Section>

      <Section title="How can we reach you?">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={lbl}>Email</label><input type="email" className={inp} value={f.email} onChange={set("email")} data-testid="audit-email" /></div>
          <div><label className={lbl}>Phone</label><input type="tel" className={inp} value={f.phone} onChange={set("phone")} data-testid="audit-phone" /></div>
          <div className="sm:col-span-2"><label className={lbl}>Preferred contact method</label>
            <select className={inp} value={f.preferred_contact} onChange={set("preferred_contact")} data-testid="audit-contact-method">{AUDIT_OPTIONS.preferred_contact.map((o) => <option key={o}>{o}</option>)}</select></div>
        </div>
      </Section>

      <Section title="Your current lead process">
        <label className={lbl}>How do you currently generate leads?</label>
        <div className="flex flex-wrap gap-2 mb-4">
          {AUDIT_OPTIONS.lead_sources.map((s) => (
            <button type="button" key={s} onClick={() => toggleSource(s)} data-testid={`audit-source-${s.replace(/\W+/g,'-').toLowerCase()}`}
              className={`px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${f.lead_sources.includes(s) ? "bg-alto-red text-white border-alto-red" : "border-slate-300 text-slate-600 hover:border-alto-red"}`}>{s}</button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Select label="Leads per month" value={f.leads_per_month} onChange={set("leads_per_month")} opts={AUDIT_OPTIONS.leads_per_month} tid="audit-leads-month" />
          <Select label="How are leads tracked?" value={f.tracking_method} onChange={set("tracking_method")} opts={AUDIT_OPTIONS.tracking_method} tid="audit-tracking" />
          <Select label="How fast is first follow-up?" value={f.follow_up_speed} onChange={set("follow_up_speed")} opts={AUDIT_OPTIONS.follow_up_speed} tid="audit-speed" />
          <Select label="Average new customer value" value={f.customer_value} onChange={set("customer_value")} opts={AUDIT_OPTIONS.customer_value} tid="audit-value" />
          <div className="sm:col-span-2"><Select label="Biggest challenge" value={f.biggest_challenge} onChange={set("biggest_challenge")} opts={AUDIT_OPTIONS.biggest_challenge} tid="audit-challenge" /></div>
        </div>
      </Section>

      <label className="flex items-start gap-3 text-sm text-slate-600">
        <input type="checkbox" checked={f.consent} onChange={(e) => { const c = e.target.checked; setF((p) => ({ ...p, consent: c })); }} className="mt-0.5 w-4 h-4 accent-red-600" data-testid="audit-consent" />
        I agree to be contacted by AllToMAIDIt about my audit.
      </label>

      <button type="submit" disabled={busy} data-testid="audit-submit" className="btn-red w-full font-bold px-6 py-4 rounded-md inline-flex items-center justify-center gap-2 text-sm disabled:opacity-60">
        {busy ? "Submitting…" : <>GET MY FREE AUDIT <ArrowRight size={18} /></>}
      </button>
      <p className="text-center text-xs text-slate-400">No cost. No obligation. Your information stays private.</p>
    </form>
  );
}

function Section({ title, children }) {
  return <div><h4 className="font-display font-bold text-slate-900 mb-3">{title}</h4>{children}</div>;
}
function Select({ label, value, onChange, opts, tid }) {
  return <div><label className={lbl}>{label}</label><select className={inp} value={value} onChange={onChange} data-testid={tid}><option value="">Select…</option>{opts.map((o) => <option key={o}>{o}</option>)}</select></div>;
}
